// components/InvoicePreview.js
import React, { forwardRef } from 'react';
import { formatNumber } from '../utils/formatters';

const InvoicePreview = forwardRef(({
  template,
  companyInfo,
  customerInfo,
  invoiceInfo,
  items,
  subtotal,
  taxAmount,
  total,
  taxRate,
  notes
}, ref) => {
  
  return (
    <div 
      ref={ref} 
      className="rounded-lg shadow-lg print-content mx-auto
        /* 모바일: 작은 미리보기 - A4 비율 유지 */
        p-4 w-full min-h-[400px] max-w-full overflow-hidden
        /* 데스크탑: 실제 A4 사이즈를 축소하여 미리보기 */
        lg:p-8 lg:w-[210mm] lg:min-h-[297mm] lg:overflow-visible lg:max-w-none lg:transform lg:origin-top-left"

      style={{
        backgroundImage: `url(${template.bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
        /* 모바일에서만 A4 비율 강제, 데스크탑에서는 자연스러운 높이 */
          transform: window.innerWidth >= 1024 ? 'scale(0.77)' : 'none'
      }}
    >
      {/* 헤더 */}
      <div className={`${template.headerStyle} p-4 lg:p-6 lg:mb-6 rounded-t-lg`}>
        <div className="flex justify-between items-center">
          <div>
            <h1 
              className={`text-xl lg:text-2xl font-bold ${template.titleColor}`}
              style={template.customStyles?.title || {}}
            >
              {invoiceInfo.title}
            </h1>
            {invoiceInfo.invoiceNumber && (
              <p className="text-xs lg:text-base text-gray-600 mt-1">
                No. {invoiceInfo.invoiceNumber}
              </p>
            )}
          </div>
          <div className="text-right text-xs lg:text-base text-gray-600">
            <p>{invoiceInfo.date}</p>
          </div>
        </div>
      </div>

      {/* 업체 및 고객 정보 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-8 m-2 lg:m-4 my-4 lg:my-10">
        <div>
          <h3 className="font-semibold text-gray-800 mb-1 lg:mb-2 border-b pb-1 text-sm lg:text-base">발행업체</h3>
          <div className="text-xs lg:text-sm space-y-0.5 lg:space-y-1" style={template.customStyles?.companyInfo || {}}>
            <p className="font-medium">{companyInfo.name || '업체명'}</p>
            <p>{companyInfo.address || '주소'}</p>
            <p>{companyInfo.phone || '전화번호'}</p>
            {companyInfo.email && <p>{companyInfo.email}</p>}
            {companyInfo.businessNumber && <p>사업자번호: {companyInfo.businessNumber}</p>}
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-gray-800 mb-1 lg:mb-2 border-b pb-1 text-sm lg:text-base">고객정보</h3>
          <div className="text-xs lg:text-sm space-y-0.5 lg:space-y-1" style={template.customStyles?.customerInfo || {}}>
            <p className="font-medium">{customerInfo.name || '고객명'}</p>
            <p>{customerInfo.address || '주소'}</p>
            <p>{customerInfo.phone || '연락처'}</p>
          </div>
        </div>
      </div>

      {/* 품목 테이블 */}
      <div className="my-4 lg:my-10 mx-2 lg:mx-4 overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-10" style={template.customStyles?.tableHeader || {}}>
              <th className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-left font-semibold text-xs lg:text-sm">품목/서비스</th>
              <th className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-right font-semibold text-xs lg:text-sm">단가</th>
              <th className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-right font-semibold text-xs lg:text-sm">수량</th>
              <th className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-right font-semibold text-xs lg:text-sm">금액</th>
            </tr>
          </thead>
          <tbody style={template.customStyles?.tableBody || {}}>
            {items.map((item) => (
                <tr key={item.id}>
                  <td className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-xs lg:text-sm">{item.description || '품목명'}</td>
                  <td className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-right text-xs lg:text-sm">{formatNumber(item.unitPrice)}원</td>
                  <td className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-right text-xs lg:text-sm">{item.quantity}</td>
                  <td className="border border-gray-300 px-1 lg:px-2 py-1 lg:py-1 text-right text-xs lg:text-sm">{formatNumber(item.amount)}원</td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 합계 */}
      <div className="flex justify-end my-1 lg:my-2">
        <div className="w-60 lg:w-80">
          <div className="space-y-1 lg:space-y-2 text-xs lg:text-sm" style={template.customStyles?.totalSection || {}}>
            <div className="flex justify-between border-b pb-1 lg:pb-2">
              <span>소계:</span>
              <span>{formatNumber(subtotal)}원</span>
            </div>
            <div className="flex justify-between">
              <span>부가세 ({taxRate}%):</span>
              <span>{formatNumber(taxAmount)}원</span>
            </div>
            <div className="flex justify-between border-t pt-1 lg:pt-2 font-bold text-sm lg:text-lg">
              <span>총계:</span>
              <span>{formatNumber(total)}원</span>
            </div>
          </div>
        </div>
      </div>

      {/* 비고 */}
      {notes && (
        <div className="mt-3 lg:mt-6">
          <h4 className="font-semibold text-gray-800 mb-1 lg:mb-2 text-sm lg:text-base">비고</h4>
          <div className="text-xs lg:text-sm text-gray-600 bg-gray-50 p-2 lg:p-3 rounded" style={template.customStyles?.notes || {}}>
            {notes}
          </div>
        </div>
      )}
    </div>
  );
});

InvoicePreview.displayName = 'InvoicePreview';

export default InvoicePreview;